import type { IProductTagTheme } from "@/api/AppApi/services/company/interfaces/ICompany";

export interface IResolvedProductTag {
  id: string;
  name: string;
  bgColor: string;
  textColor: string;
}

/** Used when a configured tag has no (or an unparsable) colour, so a tag never renders invisible. */
const FALLBACK_BG = "var(--primary-color)";
const FALLBACK_TEXT = "var(--primary-inner-color)";

const LIGHT_TEXT = "#ffffff";
const DARK_TEXT = "#1f1f1f";

const HEX = /^#?(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

const toId = (id: number | string | null | undefined): string | null => {
  if (typeof id === "number") return Number.isFinite(id) ? String(id) : null;
  const trimmed = id?.trim();
  return trimmed ? trimmed : null;
};

/** `#abc` / `abc` / `#AABBCC` → `#aabbcc`; anything else → `null`. */
export const normalizeHexColor = (
  color: string | null | undefined,
): string | null => {
  const raw = color?.trim();
  if (!raw || !HEX.test(raw)) return null;
  const hex = raw.startsWith("#") ? raw.slice(1) : raw;
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;
  return `#${full.toLowerCase()}`;
};

/**
 * Merchants pick a single colour per tag, so the label colour has to be derived.
 *
 * Uses YIQ perceived brightness rather than WCAG relative luminance: the two agree
 * almost everywhere, but WCAG's crossover point sits just above mid-saturation reds
 * (`#dc3545` lands at L≈0.182 vs. a 0.179 threshold) and would print them near-black,
 * against every convention for a "Sale" badge. YIQ matches what merchants expect.
 */
export const getReadableTextColor = (normalizedHex: string): string => {
  const r = parseInt(normalizedHex.slice(1, 3), 16);
  const g = parseInt(normalizedHex.slice(3, 5), 16);
  const b = parseInt(normalizedHex.slice(5, 7), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness >= 128 ? DARK_TEXT : LIGHT_TEXT;
};

/**
 * Intersects the tag ids assigned to a product with the tags the merchant configured
 * under Theme > Product > Product Tags, and resolves each one's display colours.
 *
 * The product payload carries ids only — every label and colour comes from the theme
 * config, which the backend already prunes of deleted tags and re-enriches with the
 * current tag name.
 *
 * Behaviour the settings screen depends on:
 * - theme order wins, so a tag sits in the same position on every card;
 * - a tag id with no matching config is never shown (and vice versa);
 * - an empty result means "render nothing", not "render an empty container".
 */
export const getVisibleProductTags = (
  tagIds: number[] | null | undefined,
  themeTags: IProductTagTheme[] | null | undefined,
): IResolvedProductTag[] => {
  if (!Array.isArray(tagIds) || tagIds.length === 0) return [];
  if (!Array.isArray(themeTags) || themeTags.length === 0) return [];

  const assigned = new Set<string>();
  for (const tagId of tagIds) {
    const id = toId(tagId);
    if (id) assigned.add(id);
  }
  if (assigned.size === 0) return [];

  const resolved: IResolvedProductTag[] = [];
  const seen = new Set<string>();

  for (const configured of themeTags) {
    const id = toId(configured?.tag_id);
    if (!id || seen.has(id) || !assigned.has(id)) continue;
    seen.add(id);

    const name = configured.name?.trim();
    if (!name) continue;

    const bgColor = normalizeHexColor(configured.color);
    resolved.push({
      id,
      name,
      bgColor: bgColor ?? FALLBACK_BG,
      textColor: bgColor ? getReadableTextColor(bgColor) : FALLBACK_TEXT,
    });
  }

  return resolved;
};
